import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { BarChart3, RefreshCw, Loader2, TrendingUp, TrendingDown, Minus, CheckCircle2, XCircle, Activity, Target, Zap, AlertCircle } from 'lucide-react';
import { api } from '../services/api';

interface AvatarStat { role: string; totalContributions: number; accepted: number; acceptanceRate: number; }
interface AggregateData {
  sessions: { total: number; active: number; completed: number; archived: number; thisMonth: number; thisWeek: number; };
  quality: {
    sessionsAnalysed: number; coverageRate: number;
    avgEngagementScore: number | null; goalAttainmentRate: number | null;
    avgContributionsPerSession: number | null; avgAcceptanceRate: number | null;
    avgFrustrationScore: number | null;
    engagementDistribution: { high: number; medium: number; low: number; };
  };
  avatarStats: AvatarStat[];
  generatedAt: string;
}

function StatCard({ label, value, sub, icon: Icon, color = 'primary' }: { label: string; value: string | number; sub?: string; icon: React.ElementType; color?: string }) {
  const colorMap: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-500',
    slate: 'bg-slate-100 text-slate-500',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex gap-4 items-start">
      <div className={`p-2.5 rounded-xl shrink-0 ${colorMap[color] || colorMap.primary}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-3xl font-bold text-navy mt-0.5 leading-none">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function pct(v: number | null) { return v !== null ? `${Math.round(v * 100)}%` : '—'; }
function num(v: number | null, dp = 0) { return v !== null ? v.toFixed(dp) : '—'; }

function frustrationClass(score: number) {
  if (score > 10) return 'text-red-500';
  if (score > 5) return 'text-amber-500';
  return 'text-emerald-600';
}

function acceptanceClass(rate: number) {
  if (rate >= 0.6) return 'text-emerald-600';
  if (rate >= 0.35) return 'text-amber-600';
  return 'text-red-500';
}

function EngagementBadge({ score }: { score: number | null }) {
  if (score === null) return <span className="text-slate-400 text-sm">—</span>;
  const isHigh = score >= 0.65; const isLow = score < 0.4;
  let Icon = Minus;
  let label = 'Medium';
  let cls = 'text-amber-600 bg-amber-50 border-amber-200';
  if (isHigh) { Icon = TrendingUp; label = 'High'; cls = 'text-emerald-600 bg-emerald-50 border-emerald-200'; }
  else if (isLow) { Icon = TrendingDown; label = 'Low'; cls = 'text-red-600 bg-red-50 border-red-200'; }
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${cls}`}>
      <Icon size={14} /> {label} <span className="opacity-70 font-normal">{Math.round(score * 100)}%</span>
    </span>
  );
}

const DIST_COLORS = { high: '#10b981', medium: '#f59e0b', low: '#ef4444' };

export const AnalyticsDashboard: React.FC = () => {
  const [data, setData] = useState<AggregateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get<AggregateData>('/api/admin/analytics/aggregate');
      setData(res.data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load analytics');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex-1 overflow-y-auto p-8 font-display flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-slate-400" />
    </div>
  );

  if (error) return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
        <AlertCircle size={16} className="shrink-0" /> {error}
      </div>
    </div>
  );

  if (!data) return null;

  const { sessions, quality, avatarStats } = data;

  const statusData = [
    { name: 'Active', value: sessions.active, fill: '#00c7c4' },
    { name: 'Completed', value: sessions.completed, fill: '#6366f1' },
    { name: 'Archived', value: sessions.archived, fill: '#94a3b8' },
  ];

  const distData = [
    { name: 'High', value: quality.engagementDistribution.high, fill: DIST_COLORS.high },
    { name: 'Medium', value: quality.engagementDistribution.medium, fill: DIST_COLORS.medium },
    { name: 'Low', value: quality.engagementDistribution.low, fill: DIST_COLORS.low },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <BarChart3 size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Platform Analytics</h1>
            <p className="text-slate-500 text-sm font-medium">
              Aggregate session and avatar intelligence
              {data.generatedAt && <> · as of {new Date(data.generatedAt).toLocaleTimeString()}</>}
            </p>
          </div>
        </div>
        <button onClick={load} className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
          <RefreshCw size={16} /> Refresh
        </button>
      </header>

      {/* Platform Health */}
      <section>
        <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">Platform Health</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Sessions" value={sessions.total} sub={`${sessions.thisMonth} this month`} icon={BarChart3} color="primary" />
          <StatCard label="Active Now" value={sessions.active} sub={`${sessions.completed} completed`} icon={Activity} color="emerald" />
          <StatCard label="This Week" value={sessions.thisWeek} icon={Zap} color="amber" />
          <StatCard label="Sessions Analysed" value={quality.sessionsAnalysed} sub={`${pct(quality.coverageRate)} coverage`} icon={Target} color={quality.coverageRate >= 0.5 ? 'emerald' : 'amber'} />
        </div>
      </section>

      {/* Session Quality */}
      <section>
        <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">Session Quality</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Avg Engagement</p>
            <EngagementBadge score={quality.avgEngagementScore} />
            <p className="text-xs text-slate-400 mt-3">Across {quality.sessionsAnalysed} analysed sessions</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Goal Attainment</p>
            {quality.goalAttainmentRate !== null ? (
              <div className="flex items-center gap-2">
                {quality.goalAttainmentRate >= 0.6
                  ? <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
                  : <XCircle size={20} className="text-red-400 shrink-0" />
                }
                <span className="text-3xl font-bold text-navy">{pct(quality.goalAttainmentRate)}</span>
                <span className="text-xs text-slate-400">of goals met</span>
              </div>
            ) : <span className="text-slate-400 text-sm">No data yet</span>}
          </div>
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Contributions</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-navy">{num(quality.avgContributionsPerSession)}</span>
              <span className="text-xs text-slate-400">avg per session</span>
            </div>
            {quality.avgAcceptanceRate !== null && (
              <p className="text-xs text-slate-500 mt-2">
                <span className="font-semibold text-emerald-600">{pct(quality.avgAcceptanceRate)}</span> avg acceptance rate
              </p>
            )}
            {quality.avgFrustrationScore !== null && (
              <p className="text-xs text-slate-500 mt-1">
                Frustration signal: <span className={`font-semibold ${frustrationClass(quality.avgFrustrationScore)}`}>{num(quality.avgFrustrationScore, 1)}</span>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Charts */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Sessions by Status</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={statusData} barCategoryGap="40%">
              <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Engagement Distribution</p>
          {quality.sessionsAnalysed > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={distData} barCategoryGap="40%">
                <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
                <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v} sessions`, 'Count']} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {distData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-44 text-slate-400 text-sm">Run analytics on sessions to see distribution</div>
          )}
        </div>
      </section>

      {/* Avatar Role Performance */}
      {avatarStats.length > 0 && (
        <section>
          <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">Avatar Role Performance</p>
          <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                  <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Avatar Role</th>
                  <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Contributions</th>
                  <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Accepted</th>
                  <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Acceptance Rate</th>
                  <th className="px-5 py-3 w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {avatarStats.map(a => (
                  <tr key={a.role} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-3 font-medium text-navy capitalize">{a.role.replace(/_/g, ' ')}</td>
                    <td className="px-5 py-3 text-right text-slate-500">{a.totalContributions.toLocaleString()}</td>
                    <td className="px-5 py-3 text-right text-slate-500">{a.accepted.toLocaleString()}</td>
                    <td className="px-5 py-3 text-right font-semibold">
                      <span className={acceptanceClass(a.acceptanceRate)}>
                        {pct(a.acceptanceRate)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round(a.acceptanceRate * 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      </div>
    </div>
  );
};

export default AnalyticsDashboard;
